import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { customerApi } from "../lib/customerApi";
import { formatINR } from "../lib/api";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

const TIER_BLURB = {
    muse: "Welcome to Sukaanyas. Every order takes you closer to Patron.",
    patron: "Thank you for being a Patron — enjoy 5% off storewide, auto-applied.",
    circle: "You're in the Sukaanyas Circle — 10% off, free shipping and early access on every drop.",
};

export default function AccountLoyalty() {
    const { user } = useCustomerAuth();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        customerApi.get("/me/loyalty").then(({ data }) => setData(data)).catch(() => setData(null));
    }, []);

    if (!user) return <div className="p-10 text-inkMuted">Please sign in to view your loyalty dashboard.</div>;
    if (!data) return <div className="p-10 text-inkMuted">Loading…</div>;

    const pct = data.nextTier
        ? Math.min(100, Math.floor((data.tierProgressPaise / (data.nextTier.thresholdPaise || 1)) * 100))
        : 100;
    const toGo = data.nextTier ? Math.max(0, (data.nextTier.thresholdPaise - data.tierProgressPaise) / 100) : 0;
    const share = data.referralCode
        ? `https://wa.me/?text=${encodeURIComponent(`Shop Sukaanyas — use my code ${data.referralCode} when you sign up. ${window.location.origin}/signup?ref=${data.referralCode}`)}`
        : null;

    const copyRef = async () => {
        if (!data.referralCode) return;
        try { await navigator.clipboard.writeText(data.referralCode); setCopied(true); toast.success("Referral code copied"); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Could not copy"); }
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="account-loyalty-page">
            <Header />
            <main className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12">
                <span className="label-eyebrow">My Sukaanyas</span>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight text-inkDark">Loyalty dashboard</h1>

                <section className="mt-10 bg-beige rounded-2xl p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                        <div>
                            <span className="label-eyebrow">Current tier</span>
                            <div className="mt-2 font-display text-4xl capitalize">{data.tier}</div>
                            <p className="mt-1 text-inkMuted max-w-xl">{TIER_BLURB[data.tier] || TIER_BLURB.muse}</p>
                        </div>
                        {data.nextTier ? (
                            <div className="text-right">
                                <div className="label-eyebrow text-[10px]">Next tier</div>
                                <div className="font-display text-xl capitalize">{data.nextTier.name}</div>
                                <div className="text-xs text-inkMuted mt-1">{formatINR(toGo)} to go</div>
                            </div>
                        ) : <div className="label-eyebrow text-[10px]">Top tier achieved ✦</div>}
                    </div>
                    <div className="mt-5 h-2 w-full bg-inkDark/10 rounded-full overflow-hidden">
                        <div className="h-full bg-skyblueDeep transition-all" style={{ width: `${pct}%` }} data-testid="tier-progress-bar" />
                    </div>
                </section>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Stat label="Lifetime spend" value={formatINR(data.lifetimeSpendPaise / 100)} testId="loyalty-lifetime" />
                    <Stat label="Style Points" value={data.pointsEnabled ? data.stylePoints : "—"} sub={data.pointsEnabled ? "For tier progression" : "Currently paused"} testId="loyalty-points" />
                    <Stat label="Style Coins" value={data.coinsEnabled ? data.styleCoins : "—"} sub={data.coinsEnabled ? "Redeem up to 20% at checkout" : "Currently paused"} testId="loyalty-coins" />
                </div>

                {data.referralsEnabled && data.referralCode ? (
                    <section className="mt-10 bg-ivory border border-inkDark/5 rounded-2xl p-6" data-testid="referral-card">
                        <span className="label-eyebrow">Refer a friend</span>
                        <h2 className="mt-2 font-display text-2xl">Share Sukaanyas, earn coins.</h2>
                        <p className="text-sm text-inkMuted mt-1">When a friend signs up with your code and completes their first paid order, you earn 500 Style Coins.</p>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <code className="px-4 py-2 rounded-xl bg-beige text-lg tracking-widest font-mono" data-testid="referral-code">{data.referralCode}</code>
                            <button onClick={copyRef} className="btn-pill text-xs" data-testid="referral-copy-btn">{copied ? "Copied!" : "Copy"}</button>
                            {share ? <a href={share} target="_blank" rel="noreferrer" className="btn-pill text-xs" data-testid="referral-wa-share">Share on WhatsApp</a> : null}
                        </div>
                    </section>
                ) : null}

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <HistoryList title="Style Points — recent activity" items={data.pointsHistory} />
                    <HistoryList title="Style Coins — recent activity" items={data.coinsHistory} />
                </div>

                <div className="mt-10 text-sm text-inkMuted">
                    Learn more in the{" "}
                    <Link to="/policy/loyalty" className="text-skyblueDeep underline">Loyalty programme terms</Link>.
                </div>
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}

function Stat({ label, value, sub, testId }) {
    return (
        <div className="bg-ivory border border-inkDark/5 rounded-2xl p-5" data-testid={testId}>
            <div className="label-eyebrow text-[10px]">{label}</div>
            <div className="font-display text-3xl mt-1">{value}</div>
            {sub ? <div className="text-xs text-inkMuted mt-1">{sub}</div> : null}
        </div>
    );
}

function HistoryList({ title, items }) {
    return (
        <div>
            <div className="label-eyebrow mb-3">{title}</div>
            <ul className="space-y-2 text-sm">
                {items?.length ? items.map((e) => (
                    <li key={e.id} className="flex justify-between bg-beige rounded-xl px-4 py-2">
                        <span className="capitalize">{(e.type || "").replace(/_/g, " ")}</span>
                        <span className={e.delta >= 0 ? "text-skyblueDeep" : "text-red-600"}>
                            {e.delta > 0 ? "+" : ""}{e.delta}
                        </span>
                    </li>
                )) : <li className="text-inkMuted text-xs">No entries yet.</li>}
            </ul>
        </div>
    );
}
